import { Game2048State } from '../../../types/game.types'

export const GRID_SIZE = 4
export const WINNING_VALUE = 2048

export const createInitialState = (): Game2048State => {
  const grid = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0))
  const newGrid = addRandomTile(addRandomTile(grid))
  
  return {
    grid: newGrid,
    score: 0,
    bestScore: 0,
    gameOver: false,
    won: false,
    moves: 0,
    lastMove: null
  }
}

export const addRandomTile = (grid: number[][]): number[][] => {
  const emptyCells: [number, number][] = []
  
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (grid[i][j] === 0) {
        emptyCells.push([i, j])
      }
    }
  }
  
  if (emptyCells.length > 0) {
    const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)]
    const newGrid = grid.map(r => [...r])
    newGrid[row][col] = Math.random() < 0.9 ? 2 : 4
    return newGrid
  }
  
  return grid
}

export const canMove = (grid: number[][]): boolean => {
  // Check for empty cells
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (grid[i][j] === 0) {
        return true
      }
    }
  }
  
  // Check for possible merges
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      const value = grid[i][j]
      
      // Check right neighbor
      if (j < GRID_SIZE - 1 && grid[i][j + 1] === value) {
        return true
      }
      
      // Check bottom neighbor
      if (i < GRID_SIZE - 1 && grid[i + 1][j] === value) {
        return true
      }
    }
  }
  
  return false
}

export const checkWin = (grid: number[][]): boolean => {
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (grid[i][j] === WINNING_VALUE) {
        return true
      }
    }
  }
  return false
}

export const moveLeft = (grid: number[][]): { grid: number[][], score: number } => {
  const newGrid = grid.map(row => [...row])
  let score = 0
  
  for (let i = 0; i < GRID_SIZE; i++) {
    // Remove zeros and merge
    const nonZero = newGrid[i].filter(cell => cell !== 0)
    const merged: number[] = []
    
    for (let j = 0; j < nonZero.length; j++) {
      if (j < nonZero.length - 1 && nonZero[j] === nonZero[j + 1]) {
        merged.push(nonZero[j] * 2)
        score += nonZero[j] * 2
        j++
      } else {
        merged.push(nonZero[j])
      }
    }
    
    // Pad with zeros
    while (merged.length < GRID_SIZE) {
      merged.push(0)
    }
    
    newGrid[i] = merged
  }
  
  return { grid: newGrid, score }
}

export const moveRight = (grid: number[][]): { grid: number[][], score: number } => {
  const newGrid = grid.map(row => [...row].reverse())
  const { grid: movedGrid, score } = moveLeft(newGrid)
  return { grid: movedGrid.map(row => row.reverse()), score }
}

export const moveUp = (grid: number[][]): { grid: number[][], score: number } => {
  // Transpose, move left, transpose back
  const transposed = transpose(grid)
  const { grid: movedGrid, score } = moveLeft(transposed)
  return { grid: transpose(movedGrid), score }
}

export const moveDown = (grid: number[][]): { grid: number[][], score: number } => {
  // Transpose, move right, transpose back
  const transposed = transpose(grid)
  const { grid: movedGrid, score } = moveRight(transposed)
  return { grid: transpose(movedGrid), score }
}

export const transpose = (grid: number[][]): number[][] => {
  const newGrid: number[][] = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0))
  
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      newGrid[j][i] = grid[i][j]
    }
  }
  
  return newGrid
}

export const gridsEqual = (grid1: number[][], grid2: number[][]): boolean => {
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (grid1[i][j] !== grid2[i][j]) {
        return false
      }
    }
  }
  return true
}

export const makeMove = (
  state: Game2048State,
  direction: 'up' | 'down' | 'left' | 'right'
): Game2048State => {
  if (state.gameOver) {
    return state
  }
  
  let newGrid: number[][]
  let score = 0
  
  switch (direction) {
    case 'left':
      ({ grid: newGrid, score } = moveLeft(state.grid))
      break
    case 'right':
      ({ grid: newGrid, score } = moveRight(state.grid))
      break
    case 'up':
      ({ grid: newGrid, score } = moveUp(state.grid))
      break
    case 'down':
      ({ grid: newGrid, score } = moveDown(state.grid))
      break
  }
  
  // Check if move actually changed the grid
  if (gridsEqual(state.grid, newGrid)) {
    return state
  }
  
  // Add random tile
  const gridWithNewTile = addRandomTile(newGrid)
  
  const newScore = state.score + score
  const newBestScore = Math.max(state.bestScore, newScore)
  const won = checkWin(gridWithNewTile) || state.won
  const gameOver = !canMove(gridWithNewTile)
  
  return {
    grid: gridWithNewTile,
    score: newScore,
    bestScore: newBestScore,
    gameOver,
    won,
    moves: state.moves + 1,
    lastMove: direction
  }
}

export const resetGame = (): Game2048State => {
  return createInitialState()
}